import { FormFieldNewType } from "@/components/controls/Form/types";
import { NONE_OPTION_VALUE } from "@/components/controls/Form/Form";
import { APIFieldItem } from "@/pages/settings/types";
import { UserData } from "@/entities/users/types";
import { TFunction } from "i18next";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import {
  dealConfidenceLevels,
  csmRenewalConfidenceLevels
} from "@/pages/deals/helpers";
import {
  DealConfidenceLevel,
  CsmRenewalConfidenceLevel
} from "@/pages/deals/types";
import { getEnumTKey } from "@/utils/strings";
import {
  isEntityDisplayType,
  getCreateNewLabel
} from "@/utils/entityFieldHelpers";
import {
  parseOptions,
  parseMultiSelectOptions,
  getValidationForDisplayType,
  createAddNewOptionHandler,
  CreateNewOptionContext,
  shouldShowCreateNewButton,
  shouldDisableCreateNewButton
} from "./shared";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  allowsMultipleFiles,
  getFileFieldAccept
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import { map } from "lodash";
import { getCountrySelectOptions } from "@/utils/countries";
import { hasPhoneNumber } from "@/components/controls/Form/helpers";
import { getCurrentLocale } from "@/utils/locale";

export interface CreateFormNewContext extends CreateNewOptionContext {
  users: Record<string, UserData>;
  userData: UserData;
  selectedCompanyId: string;
  selectedPersonId: string;
  setSelectedCompanyId: (id: string) => void;
  setSelectedPersonId: (id: string) => void;
  setHasUserEnteredDealName: (entered: boolean) => void;
  autoPopulateDealName: (companyName: string, productId?: string) => void;
  setView: (
    view: "deals" | "companies" | "people" | "partners" | "partnerContacts"
  ) => void;
  hasUserEnteredDealName: boolean;
  onCompanySelect?: (companyId: string) => void;
  onProductSelect?: (productId: string) => void;
  entityType?: EntityType;
  showCreateOptionModal?: boolean;
  newOptionName?: string;
  currentFieldForNewOption?: { fieldId: string; fieldKey: string } | null;
  fieldResetKeys?: Record<string, number>;
  handleCreateNewOption?: (optionName: string) => Promise<void>;
}

export const createFormFieldNewFromAPI = (
  apiField: APIFieldItem,
  t: TFunction,
  context: CreateFormNewContext
): FormFieldNewType | null => {
  const baseField: Partial<FormFieldNewType> = {
    name: apiField.fieldKey,
    label: apiField.label,
    required: apiField.required,
    type: "text",
    entityType: context.entityType,
    fieldId: apiField.id,
    helpText: apiField.helpText || undefined
  };

  const placeholderField = apiField.label || apiField.fieldKey;
  const enterPlaceholder = () =>
    t("common.enterFieldPlaceholder", { field: placeholderField });
  const selectPlaceholder = () =>
    t("common.selectFieldPlaceholder", { field: placeholderField });

  if (apiField.fieldType === "system" || apiField.fieldType === "header") {
    switch (apiField.fieldKey) {
      case "name":
        const isCompany = apiField.label?.toLowerCase()?.includes("company");
        let entityPrefix = "deals"; // default
        if (isCompany) {
          entityPrefix = "companies";
        } else if (context.entityType === EntityType.LEAD) {
          entityPrefix = "leads";
        } else if (context.entityType === EntityType.CONTRACT) {
          entityPrefix = "contracts";
        } else if (context.entityType === EntityType.PARTNER_CONTACT) {
          entityPrefix = "partnercontacts";
        } else if (context.entityType === EntityType.PARTNER) {
          entityPrefix = "partners";
        }

        return {
          ...baseField,
          type: "text",
          fieldActionProps: {
            placeholder: t(`${entityPrefix}.createModal.name`)
          },
          onBlur: (value: string) => {
            context.setHasUserEnteredDealName(
              !!value && value.trim().length > 0
            );
          }
        } as FormFieldNewType;

      case "aeConfidenceLevel":
        return {
          ...baseField,
          type: "singleSelect",
          required: false,
          fieldActionProps: {
            options: map(dealConfidenceLevels, (level) => ({
              id: level,
              name: DealConfidenceLevel[level] // Use 'name' instead of 'displayName' so formatter gets called
            })),
            formatter: (value: any) =>
              t(
                getEnumTKey(
                  "deals",
                  "levels",
                  DealConfidenceLevel,
                  value as number
                )
              ),
            renderIcon: (value: string | number) => (
              <ConfidenceLevelIcon value={Number(value)} />
            )
          }
        } as FormFieldNewType;

      case "csmRenewalConfidenceLevel":
        return {
          ...baseField,
          type: "singleSelect",
          required: false,
          fieldActionProps: {
            options: map(csmRenewalConfidenceLevels, (level) => ({
              id: level,
              name: CsmRenewalConfidenceLevel[level] // Use 'name' instead of 'displayName' so formatter gets called
            })),
            formatter: (value: any) =>
              t(
                getEnumTKey(
                  "deals",
                  "levels",
                  CsmRenewalConfidenceLevel,
                  value as number
                )
              ),
            renderIcon: (value: string | number) => (
              <ConfidenceLevelIcon value={Number(value)} />
            )
          }
        } as FormFieldNewType;
    }
  }

  if (isEntityDisplayType(apiField.displayType)) {
    const allowUsersToAdd = apiField.config?.allowUsersToAdd;
    const onCreateNew = allowUsersToAdd
      ? () => {
          if (apiField.displayType === "companies") {
            context.setView("companies");
          } else if (apiField.displayType === "people") {
            context.setView("people");
          } else if (apiField.displayType === "partners") {
            context.setView("partners");
          } else if (
            apiField.displayType === FieldDisplayType.PARTNER_CONTACTS
          ) {
            context.setView("partnerContacts");
          }
        }
      : undefined;
    const createNewLabel = allowUsersToAdd
      ? getCreateNewLabel(apiField.displayType, t)
      : undefined;

    if (
      apiField.displayType === FieldDisplayType.PARTNER_CONTACTS &&
      context.entityType === EntityType.PARTNER
    ) {
      return {
        ...baseField,
        name: apiField.fieldKey,
        required: apiField.required || false,
        type: "multiSelect",
        config: apiField.config,
        defaultValue: [],
        dependencies: ["partner"],
        fieldActionProps: {
          options: [],
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          isEntityField: true,
          displayType: apiField.displayType,
          config: apiField.config,
          onCreateNew,
          createNewLabel,
          startEmpty: context.entityType === EntityType.PARTNER
        }
      } as FormFieldNewType;
    }

    const fieldConfig: FormFieldNewType = {
      ...baseField,
      name: apiField.fieldKey,
      required: apiField.required || false,
      type: "singleSelect",
      config: apiField.config,
      fieldActionProps: {
        options: [],
        placeholder: apiField.config?.placeholder || selectPlaceholder(),
        isEntityField: true,
        displayType: apiField.displayType,
        config: apiField.config, // Pass config to EntitySelectField
        variant: "form",
        onCreateNew,
        createNewLabel,
        customOnChange:
          apiField.displayType === "companies"
            ? context.onCompanySelect
            : apiField.displayType === "products"
              ? context.onProductSelect
              : undefined
      }
    };

    // Add dependencies for entity fields
    if (apiField.displayType === "people") {
      fieldConfig.dependencies = ["companyId"];
      if (apiField.fieldKey === "reportsTo") {
        const personFieldActionProps = fieldConfig.fieldActionProps as {
          showFixedOptions?: boolean;
          excludeIds?: string[];
        };
        personFieldActionProps.showFixedOptions = true;
        if (context.selectedPersonId) {
          personFieldActionProps.excludeIds = [context.selectedPersonId];
        }
      }
    }

    if (apiField.displayType === "licenses") {
      fieldConfig.dependencies = ["productId"];
    }

    if (apiField.displayType === FieldDisplayType.PARTNER_CONTACTS) {
      fieldConfig.dependencies = ["partner"];
    }

    return fieldConfig;
  }

  switch (apiField.displayType) {
    case "text":
      return {
        ...baseField,
        type: "text",
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder()
        }
      } as FormFieldNewType;

    case "number":
      const numberValidation = getValidationForDisplayType(
        apiField.displayType,
        t,
        apiField.config
      );
      return {
        ...baseField,
        type: "number",
        ...(numberValidation && { validation: numberValidation }),
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder(),
          min: apiField.config?.minValue
            ? Number(apiField.config.minValue)
            : undefined,
          max: apiField.config?.maxValue
            ? Number(apiField.config.maxValue)
            : undefined,
          step: "any"
        }
      } as FormFieldNewType;

    case "date":
      return {
        ...baseField,
        type: "date",
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          // Required to create, but the column is nullable and undated deals exist.
          clearable:
            context.entityType === EntityType.DEAL &&
            apiField.fieldKey === "expectedCloseAt"
        }
      } as FormFieldNewType;

    case "singleSelect": {
      const shouldShowCreateNew = apiField.config?.allowUsersToAdd || false;
      const fieldKey = apiField.fieldKey;
      const fieldId = apiField.id;

      const shouldShowSearch = shouldShowCreateNew || fieldKey === "industryId";

      // The default is stored as the option name, but for id-keyed lookup-backed
      // selects (industry/sub-industry) the option value is the id — so resolve
      // the name to its id via parseOptions, the same source of truth the select
      // renders from. Array-option fields are name-keyed, so id === name and the
      // name passes through unchanged.
      const rawDefaultValue = apiField.config?.defaultValue;
      const resolvedDefaultValue =
        rawDefaultValue && rawDefaultValue !== NONE_OPTION_VALUE
          ? (parseOptions(apiField.config).find(
              (option) => option.displayName === rawDefaultValue
            )?.id ?? "")
          : "";

      return {
        ...baseField,
        type: "singleSelect",
        defaultValue: resolvedDefaultValue,
        fieldResetKey: context.fieldResetKeys?.[fieldKey] || 0,
        fieldActionProps: {
          options: parseOptions(apiField.config),
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          allowUsersToAdd: shouldShowCreateNew,
          showSearch: shouldShowSearch,
          variant: "form",
          dropdownDirection: "bottom",
          popOverFooter: shouldShowCreateNew
            ? (searchTerm: string) => {
                const showButton = shouldShowCreateNewButton(searchTerm);
                if (!showButton) {
                  return <div style={{ display: "none" }} />;
                }

                const isDisabled = shouldDisableCreateNewButton(
                  searchTerm,
                  parseOptions(apiField.config)
                );

                return (
                  <AddButtonFooter
                    buttonText={t(
                      "settings.userManagement.addNewUserModal.createNewOption"
                    )}
                    onClick={createAddNewOptionHandler(
                      context,
                      fieldId,
                      fieldKey,
                      searchTerm
                    )}
                    variant="iconNoBg"
                    disabled={isDisabled}
                  />
                );
              }
            : undefined
        }
      } as FormFieldNewType;
    }

    case "multiSelect":
      const shouldShowCreateNewMulti =
        apiField.config?.allowUsersToAdd || false;
      const fieldKeyMulti = apiField.fieldKey;
      const fieldIdMulti = apiField.id;

      return {
        ...baseField,
        type: "multiSelect",
        defaultValue: apiField.config?.defaultValue || [],
        fieldResetKey: context.fieldResetKeys?.[fieldKeyMulti] || 0,
        fieldActionProps: {
          options: parseMultiSelectOptions(apiField.config),
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          allowUsersToAdd: shouldShowCreateNewMulti,
          showSearch: shouldShowCreateNewMulti,
          variant: "form",
          popOverFooter: shouldShowCreateNewMulti
            ? (searchTerm: string) => {
                const showButton = shouldShowCreateNewButton(searchTerm);
                if (!showButton) {
                  return <div style={{ display: "none" }} />;
                }

                const isDisabled = shouldDisableCreateNewButton(
                  searchTerm,
                  parseMultiSelectOptions(apiField.config)
                );

                return (
                  <AddButtonFooter
                    buttonText={t(
                      "settings.userManagement.addNewUserModal.createNewOption"
                    )}
                    onClick={createAddNewOptionHandler(
                      context,
                      fieldIdMulti,
                      fieldKeyMulti,
                      searchTerm
                    )}
                    variant="iconNoBg"
                    disabled={isDisabled}
                  />
                );
              }
            : undefined
        }
      } as FormFieldNewType;

    case "textarea":
      return {
        ...baseField,
        type: "textEditor",
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder()
        }
      } as FormFieldNewType;

    case "dateRange":
      return {
        ...baseField,
        type: "dateRange",
        fieldActionProps: {
          fieldKey: apiField.fieldKey
        }
      } as FormFieldNewType;

    case "switch":
      return {
        ...baseField,
        type: "switch",
        defaultValue: apiField.config?.defaultValue || "option1",
        fieldActionProps: {
          options: {
            option1: apiField.config?.option1 || "Option 1",
            option2: apiField.config?.option2 || "Option 2"
          }
        }
      } as FormFieldNewType;

    case "email":
      const emailValidation = getValidationForDisplayType(
        apiField.displayType,
        t,
        apiField.config
      );

      // Check if multiple emails are allowed
      if (apiField.config?.allowMultipleEmails) {
        return {
          ...baseField,
          type: "multiEmail",
          ...(emailValidation && { validation: emailValidation }),
          fieldActionProps: {
            placeholder: apiField.config?.placeholder || enterPlaceholder(),
            maxEntries: apiField.config?.maxEntries,
            allowAddNew: apiField.config?.allowAddNew !== false
          }
        } as FormFieldNewType;
      }

      return {
        ...baseField,
        type: "email",
        ...(emailValidation && { validation: emailValidation }),
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder()
        }
      } as FormFieldNewType;

    case "url":
      const urlValidation = getValidationForDisplayType(
        apiField.displayType,
        t,
        apiField.config
      );
      return {
        ...baseField,
        type: "text",
        ...(urlValidation && { validation: urlValidation }),
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder()
        }
      } as FormFieldNewType;

    case "currency":
      const currencyValidation = getValidationForDisplayType(
        apiField.displayType,
        t,
        apiField.config
      );
      return {
        ...baseField,
        type: "number",
        ...(currencyValidation && { validation: currencyValidation }),
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder(),
          step: "any"
        }
      } as FormFieldNewType;

    case "percentage":
      const percentageValidation = getValidationForDisplayType(
        apiField.displayType,
        t,
        apiField.config
      );
      return {
        ...baseField,
        type: "number",
        isPercentage: true,
        ...(percentageValidation && { validation: percentageValidation }),
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder(),
          min: 0,
          max: 100,
          step: "any"
        }
      } as FormFieldNewType;

    case "checkListItem":
      return {
        ...baseField,
        type: "checklistItem",
        defaultValue: apiField.config?.defaultValue === "checked"
      } as FormFieldNewType;

    case "checkbox":
      const checkboxInlineText = apiField.config?.checkboxInlineText || "";
      return {
        ...baseField,
        type: "checkbox",
        defaultValue: apiField.config?.defaultValue === "checked",
        fieldActionProps: {
          // If there's checkboxInlineText, use it as the label, otherwise use the field label
          label: checkboxInlineText ? checkboxInlineText : baseField.label
        }
      } as FormFieldNewType;

    case FieldDisplayType.COUNTRIES:
      return {
        ...baseField,
        type: "singleSelect",
        fieldActionProps: {
          options: getCountrySelectOptions(getCurrentLocale()),
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          showSearch: true,
          variant: "form",
          dropdownDirection: "bottom"
        }
      } as FormFieldNewType;

    case FieldDisplayType.PHONE:
      return {
        ...baseField,
        type: "phone"
      } as FormFieldNewType;

    case "currencies":
      return {
        ...baseField,
        type: "currencySelect",
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || selectPlaceholder()
        }
      } as FormFieldNewType;

    case "fileUpload":
      return {
        ...baseField,
        type: "attachments",
        // The record stores this field as a JSON string, so the create form has
        // to encode on write the same way the detail view does — without it the
        // raw attachment array is sent as the field's value.
        isSerializedFileField: true,
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || selectPlaceholder(),
          accept: getFileFieldAccept(apiField.config?.supportedFiles),
          isSingleFile: !allowsMultipleFiles(apiField.config)
        }
      } as FormFieldNewType;

    default:
      return {
        ...baseField,
        type: "text",
        fieldActionProps: {
          placeholder: apiField.config?.placeholder || enterPlaceholder()
        }
      } as FormFieldNewType;
  }
};

// Finds the form field key for a given entity displayType (e.g. the partner or
// partner-contacts field), so callers don't have to hardcode backend field keys.
export const getFieldNameByDisplayType = (
  sections: { fields?: FormFieldNewType[] }[],
  displayType: string
): string | undefined => {
  for (const section of sections) {
    for (const field of section.fields || []) {
      const props = field.fieldActionProps as
        | { displayType?: string }
        | undefined;
      if (props?.displayType === displayType) {
        return field.name;
      }
    }
  }
  return undefined;
};

export const createFormNewSectionsFromAPI = (
  apiFields: APIFieldItem[] | undefined,
  t: TFunction,
  context: CreateFormNewContext
): { id: string; fields: FormFieldNewType[] }[] => {
  if (!apiFields) {
    return [];
  }

  const formFields: FormFieldNewType[] = [];

  apiFields
    .filter((field) => field.showInCreation)
    .sort((a, b) => a.order - b.order)
    .forEach((apiField) => {
      const formField = createFormFieldNewFromAPI(apiField, t, context);
      if (formField) {
        formFields.push(formField);
      }
    });

  const sections = [
    {
      id: "deal-fields",
      fields: formFields
    }
  ];

  const partnerFieldName = getFieldNameByDisplayType(
    sections,
    FieldDisplayType.PARTNERS
  );
  if (partnerFieldName) {
    formFields.forEach((field) => {
      const props = field.fieldActionProps as
        | { displayType?: string }
        | undefined;
      if (props?.displayType === FieldDisplayType.PARTNER_CONTACTS) {
        field.dependencies = [partnerFieldName];
        // Only surface the partner-contact field once a partner is selected.
        field.visibleWhenFieldFilled = partnerFieldName;
      }
    });
  }

  return sections;
};

export const buildMapEmptyToNullObject = (
  formSections: { id: string; fields: FormFieldNewType[] }[],
  formData: Record<string, any>
): Record<string, any> => {
  const fieldKeys: string[] = [];
  const phoneFieldKeys = new Set<string>();

  formSections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.name) {
        fieldKeys.push(field.name);
        if (field.type === "phone") phoneFieldKeys.add(field.name);
      }
    });
  });

  // Build object with field keys mapped to their values from formData
  const result: Record<string, any> = {};
  fieldKeys.forEach((key) => {
    if (formData.hasOwnProperty(key)) {
      // A phone holding only a dial code ("+44 ") has no number: submit it
      // empty so it maps to null like any other unfilled field.
      result[key] =
        phoneFieldKeys.has(key) && !hasPhoneNumber(formData[key])
          ? ""
          : formData[key];
    }
  });

  return result;
};
